import React from 'react';
import { Box } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import AddIcon from '@mui/icons-material/Add';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import TableRowsIcon from '@mui/icons-material/TableRows';
import { RibbonStartGroup } from '@univerjs/ui';
import { createColumn } from '../../api/spreadsheet';
import {
  MAX_SPREADSHEET_COLUMNS,
  MAX_SPREADSHEET_ROWS
} from './spreadsheetUtils';
import type { UniverRibbonMenusContext } from './spreadsheetTypes';

export function registerUniverRibbonMenus(ctx: UniverRibbonMenusContext): void {
  const {
    univerAPI,
    themePrimary,
    campaignId,
    viewType,
    canEditRef,
    onEnrichmentClickRef,
    onEnrichAllClickRef,
    onDeduplicateClickRef,
    addColumnInProgressRef,
    addRowInProgressRef,
    showSnackbarRef,
    headerStateRef,
    onBumpSpreadsheetDataVersionRef
  } = ctx;

  const EnrichmentPlayIconComponent = () =>
    React.createElement(PlayArrowIcon, { sx: { color: themePrimary } });
  univerAPI.registerComponent(
    'EnrichmentPlayIcon',
    EnrichmentPlayIconComponent
  );
  univerAPI
    .createMenu({
      id: 'dokai-enrichment',
      icon: 'EnrichmentPlayIcon',
      title: 'Enrichment',
      tooltip: 'Enrichment',
      order: -1000,
      action: () => {
        if (!canEditRef.current) return;
        onEnrichmentClickRef.current?.();
      }
    })
    .appendTo(RibbonStartGroup.HISTORY);

  const EnrichAllPlayIconComponent = () =>
    React.createElement(PlaylistPlayIcon, { sx: { color: themePrimary } });
  univerAPI.registerComponent('EnrichAllPlayIcon', EnrichAllPlayIconComponent);
  univerAPI
    .createMenu({
      id: 'dokai-enrich-all',
      icon: 'EnrichAllPlayIcon',
      title: 'Enrich All',
      tooltip: 'Enrich All',
      order: -999,
      action: () => {
        if (!canEditRef.current) return;
        onEnrichAllClickRef.current?.();
      }
    })
    .appendTo(RibbonStartGroup.HISTORY);

  const DeduplicateIconComponent = () =>
    React.createElement(
      Box,
      {
        sx: {
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          color: themePrimary
        }
      },
      React.createElement(ContentCopyIcon, { sx: { fontSize: 18 } }),
      React.createElement(
        Box,
        {
          sx: {
            position: 'absolute',
            right: -4,
            bottom: -4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 12,
            height: 12,
            borderRadius: '50%',
            bgcolor: 'white'
          }
        },
        React.createElement(RemoveCircleOutlineIcon, {
          sx: { fontSize: 12, color: themePrimary }
        })
      )
    );
  univerAPI.registerComponent('DeduplicateIcon', DeduplicateIconComponent);
  univerAPI
    .createMenu({
      id: 'dokai-deduplicate',
      icon: 'DeduplicateIcon',
      title: 'Deduplicate',
      tooltip: 'Deduplicate',
      order: -998.5,
      action: () => {
        if (!canEditRef.current) return;
        void onDeduplicateClickRef.current?.();
      }
    })
    .appendTo(RibbonStartGroup.HISTORY);

  const AddColumnIconComponent = () =>
    React.createElement(
      Box,
      {
        sx: {
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          color: 'black'
        }
      },
      React.createElement(ViewColumnIcon, { sx: { fontSize: 20 } }),
      React.createElement(
        Box,
        {
          sx: {
            position: 'absolute',
            right: -2,
            bottom: -2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 12,
            height: 12,
            borderRadius: '50%',
            bgcolor: 'white'
          }
        },
        React.createElement(AddIcon, { sx: { fontSize: 10 } })
      )
    );
  univerAPI.registerComponent('AddColumnIcon', AddColumnIconComponent);
  univerAPI
    .createMenu({
      id: 'dokai-add-column',
      icon: 'AddColumnIcon',
      title: 'Add Column',
      tooltip: 'Add a new column',
      order: -998,
      action: () => {
        if (!canEditRef.current) return;
        if (addColumnInProgressRef.current) return;
        addColumnInProgressRef.current = true;
        const columnNamesByIndex =
          headerStateRef.current?.columnNamesByIndex ?? [];
        const colCount = columnNamesByIndex.length;
        if (colCount >= MAX_SPREADSHEET_COLUMNS) {
          showSnackbarRef.current?.(
            `Spreadsheet is limited to ${MAX_SPREADSHEET_COLUMNS} columns.`,
            'warning'
          );
          addColumnInProgressRef.current = false;
          return;
        }
        if (colCount === 0) {
          (async () => {
            try {
              await createColumn(
                campaignId,
                {
                  col_name: 'Column 0',
                  col_idx: 0,
                  col_type: 'str',
                  description: ''
                },
                viewType
              );
              onBumpSpreadsheetDataVersionRef.current?.();
            } catch {
              showSnackbarRef.current?.('Failed to create column', 'error');
            } finally {
              addColumnInProgressRef.current = false;
            }
          })();
          return;
        }
        univerAPI.executeCommand('sheet.command.insert-col', {
          range: { startColumn: colCount, endColumn: colCount }
        });
      }
    })
    .appendTo(RibbonStartGroup.HISTORY);

  const AddRowIconComponent = () =>
    React.createElement(
      Box,
      {
        sx: {
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          color: 'black'
        }
      },
      React.createElement(TableRowsIcon, { sx: { fontSize: 20 } }),
      React.createElement(
        Box,
        {
          sx: {
            position: 'absolute',
            right: -2,
            bottom: -2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 12,
            height: 12,
            borderRadius: '50%',
            bgcolor: 'white'
          }
        },
        React.createElement(AddIcon, { sx: { fontSize: 10, color: 'black' } })
      )
    );
  univerAPI.registerComponent('AddRowIcon', AddRowIconComponent);
  univerAPI
    .createMenu({
      id: 'dokai-add-row',
      icon: 'AddRowIcon',
      title: 'Add Row',
      tooltip: 'Add a new row',
      order: -997,
      action: () => {
        if (!canEditRef.current) return;
        if (addRowInProgressRef.current) return;
        addRowInProgressRef.current = true;
        const sheet = univerAPI
          .getActiveWorkbook()
          ?.getActiveSheet()
          ?.getSheet();
        const rowCount = sheet?.getConfig()?.rowCount ?? 0;
        if (rowCount >= MAX_SPREADSHEET_ROWS) {
          showSnackbarRef.current?.(
            `Spreadsheet is limited to ${MAX_SPREADSHEET_ROWS} rows.`,
            'warning'
          );
          addRowInProgressRef.current = false;
          return;
        }
        univerAPI.executeCommand('sheet.command.insert-row', {
          range: { startRow: rowCount, endRow: rowCount }
        });
      }
    })
    .appendTo(RibbonStartGroup.HISTORY);
}
